/**
 * Un hueco retenido por el bot (spec 015 · FR-022, FR-023).
 *
 * Vive en su propio fichero porque tiene **ciclo de vida propio**: un
 * temporizador que corre cada segundo. Metido en la rejilla, ese temporizador
 * volvería a dibujar la rejilla entera una vez por segundo.
 */
import { useEffect, useState } from 'react'

import { cajaDe, type HorarioVisible } from './calendario-geometria'

/** Un hueco que el bot tiene retenido mientras alguien reserva (FR-022). */
export interface BloqueoCalendario {
    dia: string
    inicio: string
    profesionalId: number
    caducaEn: string
    conversacionId: number | null
    contacto: string | null
    enlace: string | null
}

/**
 * Un hueco retenido por el bot (spec 015 · FR-022, FR-023).
 *
 * NO ES UNA CITA, Y TIENE QUE NOTARSE
 * ───────────────────────────────────
 * Es una **pre-reserva**: alguien está a medio reservar y el bot le guarda el
 * sitio unos minutos. Pintarlo como una cita normal haría creer que hay algo
 * reservado cuando lo que hay es alguien decidiendo.
 *
 * Por eso va en gris, con el borde a rayas, **sin acciones** —no se puede mover
 * ni cancelar lo que no existe todavía— y con la cuenta atrás a la vista.
 *
 * LA CUENTA ATRÁS SE CALCULA EN EL NAVEGADOR
 * ──────────────────────────────────────────
 * El servidor manda **cuándo caduca**, no cuánto queda. Mandar «quedan 45 s»
 * sería un dato que envejece en el propio viaje y que se quedaría congelado en
 * pantalla hasta la siguiente recarga.
 */
export function Bloqueo({ b, horario }: { b: BloqueoCalendario; horario: HorarioVisible }) {
    const [ahora, setAhora] = useState(() => Date.now())
    useEffect(() => {
        const t = setInterval(() => setAhora(Date.now()), 1000)
        return () => clearInterval(t)
    }, [])

    const restan = Math.max(0, Math.round((new Date(b.caducaEn).getTime() - ahora) / 1000))
    // Ya caducado: desaparece solo, sin esperar a que se recargue la pantalla.
    // Enseñar un hueco «retenido» que ya no lo está es peor que no enseñarlo.
    if (restan <= 0) return null

    // Sin duración conocida se pinta un cuarto de hora, que es el paso de la
    // rejilla. El bot retiene un hueco, no un tramo: lo que importa es DÓNDE.
    const caja = cajaDe(b.inicio, 15, horario)
    if (!caja) return null

    const mm = String(Math.floor(restan / 60)).padStart(2, '0')
    const ss = String(restan % 60).padStart(2, '0')
    const nombre = b.contacto ?? 'alguien reservando'

    return (
        <div
            className="pointer-events-none absolute inset-x-0 overflow-hidden rounded border border-dashed border-neutral-600/70 bg-neutral-700/20 px-1.5 py-0.5 text-[10px] leading-tight text-neutral-400"
            style={{ top: `${caja.arriba}px`, height: `${caja.alto}px` }}
            title={`El asistente está reservando este hueco para ${nombre}. Caduca en ${mm}:${ss}.`}
        >
            <span className="flex items-baseline gap-1">
                <span className="tabular-nums">🔒 {b.inicio}</span>
                {/* El enlace SÍ se puede pulsar aunque la caja no: es la única
                    forma de ir a ver de qué conversación se trata. */}
                {b.enlace ? (
                    <a
                        href={b.enlace}
                        target="_blank"
                        rel="noreferrer"
                        className="pointer-events-auto truncate underline decoration-dotted hover:text-neutral-200"
                    >
                        {nombre}
                    </a>
                ) : (
                    <span className="truncate">{nombre}</span>
                )}
                <span className="ml-auto shrink-0 tabular-nums">{mm}:{ss}</span>
            </span>
        </div>
    )
}
